import React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * RadioGroup - Accessible radio button group
 * 
 * Używa Radix UI RadioGroup primitive
 * - Rozmiary: "1" | "2" | "3"
 * - Variants: "classic" | "soft" | "surface"
 * - Keyboard navigation (Arrow keys)
 * 
 * @example
 * ```tsx
 * <RadioGroup value={selected} onValueChange={setSelected}>
 *   <div className="flex items-center gap-2">
 *     <RadioGroupItem value="1" id="r1" />
 *     <Label htmlFor="r1">Opcja 1</Label>
 *   </div>
 *   <div className="flex items-center gap-2">
 *     <RadioGroupItem value="2" id="r2" />
 *     <Label htmlFor="r2">Opcja 2</Label>
 *   </div>
 * </RadioGroup>
 * ```
 */

const radioGroupItemVariants = cva(
  cn(
    "peer shrink-0",
    "aspect-square rounded-full",
    "inline-flex items-center justify-center",
    "border transition-all duration-150",
    "focus-visible:outline-none focus-visible:ring-2",
    "focus-visible:ring-[var(--pink-9)] focus-visible:ring-offset-2",
    "focus-visible:ring-offset-[var(--dark-1)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "data-[state=checked]:border-[var(--pink-9)]"
  ),
  {
    variants: {
      variant: {
        classic: cn(
          "bg-[var(--dark-3)]",
          "border-[var(--dark-a7)]",
          "hover:border-[var(--pink-a6)]"
        ),
        soft: cn(
          "bg-[var(--gray-a3)]",
          "border-transparent",
          "hover:bg-[var(--gray-a4)]"
        ),
        surface: cn(
          "bg-[var(--dark-a2)]",
          "border-[var(--dark-a5)]",
          "hover:border-[var(--pink-a5)]"
        ),
      },
      size: {
        "1": "h-[16px] w-[16px]",
        "2": "h-[20px] w-[20px]",
        "3": "h-[24px] w-[24px]",
      },
    },
    defaultVariants: {
      variant: "classic",
      size: "2",
    },
  }
)

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

export interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variant, size, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ variant, size }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <div 
          className={cn(
            "rounded-full bg-[var(--pink-9)]",
            size === "1" && "h-[8px] w-[8px]",
            size === "2" && "h-[10px] w-[10px]",
            size === "3" && "h-[12px] w-[12px]"
          )} 
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem, radioGroupItemVariants }

